import { useEffect, useState } from "react";
import { Search, Plus, Edit, Trash2, Tag, Eye } from "lucide-react";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import { TemplateModal } from "../components/message-template/TemplateModal.jsx";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux.js";
import {
  deleteTemplate,
  fetchTemplates,
  setTemplatesFilters,
} from "../../store/features/templates/templatesActions.js";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../store/features/ui/uiActions.js";
import { PreviewTemplateModal } from "../components/message-template/PreviewTemplateModal.jsx";

export function TemplatesPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { filteredTemplates, filters, isLoading, isDeleting, error } =
    useAppSelector((state) => state.templates);

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  //preview template
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewTemplate(null);
  };
  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(showErrorNotification(error.message));
    }
  }, [error, dispatch]);

  const handleFilterChange = (filterName, value) => {
    dispatch(setTemplatesFilters({ [filterName]: value }));
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    swal({
      title: t("are_you_sure"),
      text: t("delete_template_confirm"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await dispatch(deleteTemplate(templateId));
          dispatch(showSuccessNotification(t("template_deleted_success")));
        } catch (error) {
          dispatch(showErrorNotification(t("failed_to_delete_template")));
        }
      }
    });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Filters */}
      <div className=" mb-4">
        <div
          className="card-body"
          style={{ backgroundColor: "white", borderRadius: "5px" }}
        >
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="row g-3">
                <div className="col-auto">
                  <select
                    value={filters.media}
                    onChange={(e) =>
                      handleFilterChange("media", e.target.value)
                    }
                    className="form-select form-select-sm">
                    <option value="all">{t("all_media")}</option>
                    <option value="sms">{t("sms")}</option>
                    <option value="email">{t("email")}</option>
                    <option value="whatsapp">{t("whatsapp")}</option>
                    <option value="telegram">{t("telegram")}</option>
                  </select>
                </div>

                <div className="col-auto">
                  <select
                    value={filters.lang_id}
                    onChange={(e) =>
                      handleFilterChange("lang_id", e.target.value)
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="all">{t("all_languages")}</option>
                    <option value="en">{t("english")}</option>
                    <option value="fr">{t("french")}</option>
                  </select>
                </div>

                <div className="col-auto">
                  <div className="position-relative">
                    <Search
                      className="position-absolute text-muted"
                      size={16}
                      style={{
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                    <input
                      type="text"
                      placeholder={t("search_templates")}
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                      className="form-control form-control-sm ps-5"
                      style={{ width: "250px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4 text-end">
              <button
                onClick={handleNewTemplate}
                className="btn btn-primary btn-sm d-flex align-items-center gap-2 ms-auto"
              >
                <Plus size={16} />
                {t("new_message_template")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="row g-4">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="col-md-6">
            <div className="card template-card h-100">
              <div className="card-body">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="card-title fw-semibold mb-2">
                      {template.event_type.replace(/_/g, " ")}
                    </h6>
                    <small className="text-muted text-uppercase fw-medium d-block mb-1">
                      {template.event_type}
                    </small>
                    <div className="d-flex gap-2">
                      <span
                        className={`badge media-badge media-${template.media}`}
                      >
                        {template.media}
                      </span>
                      <span
                        className={`badge language-badge lang-${template.lang_id}`}
                      >
                        {template.lang_id}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Subject (for email) */}
                {template.subject && (
                  <div className="mb-3">
                    <small className="text-muted text-uppercase fw-medium d-block mb-1">
                      {t("subject")}
                    </small>
                    <p className="small text-dark mb-0">{template.subject}</p>
                  </div>
                )}

                {/* Content Preview */}
                <div className="mb-3">
                  <small className="text-muted text-uppercase fw-medium d-block mb-1">
                    {t("content")}
                  </small>
                  <p className="small text-dark line-clamp-3 mb-0">
                    {template.content}
                  </p>
                </div>

                {/* Placeholders */}
                <div className="mb-3">
                  {/* <small className="text-muted text-uppercase fw-medium d-block mb-2">
                    {t("placeholders")}
                  </small> */}
                  <div className="d-flex flex-wrap gap-1">
                    {/* {template.placeholders.map((placeholder) => (
                      <span key={placeholder} className="placeholder-chip">
                        <Tag size={12} />
                        {placeholder}
                      </span>
                    ))} */}
                  </div>
                </div>

                {/* Actions */}
                <div className="d-flex gap-2 pt-3 border-top">
                  <button
                    onClick={() => handleEditTemplate(template)}
                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1">
                    <Edit size={14} />
                    {t("edit")}
                  </button>
                  <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => handlePreviewTemplate(template)}>
                    <Eye size={14} />
                    {t("preview")}
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)} className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1" disabled={isDeleting}>
                    <Trash2 size={14} />
                    {t("delete")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-5">
          <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: "64px", height: "64px" }}>
            <Search className="text-muted" size={32} />
          </div>
          <h5 className="fw-medium text-dark mb-2">
            {t("no_templates_found")}
          </h5>
          <p className="text-muted mb-3">{t("try_adjusting_filters")}</p>
          <button onClick={handleNewTemplate} className="btn btn-primary">
            {t("create_first_template")}
          </button>
        </div>
      )}

      {/* Template Modal */}
      {isModalOpen && (
        <TemplateModal template={editingTemplate} onClose={handleModalClose} />
      )}
      {isPreviewOpen && (
        <PreviewTemplateModal template={previewTemplate} onClose={handleClosePreview} />
      )}
    </div>
  );
}
